import { Platform } from 'react-native';

const loaded = new Set<string>();

function loadOnce(id: string, load: () => void): void {
  if (Platform.OS !== 'web' || loaded.has(id)) return;
  loaded.add(id);
  load();
}

/** Match link tags from index.html and injected tags (ignore query string). */
function normalizeStylesheetPath(href: string): string {
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    return new URL(href, base).pathname;
  } catch {
    return href.split('?')[0];
  }
}

function findExistingStylesheetLink(href: string, id: string): HTMLLinkElement | null {
  if (typeof document === 'undefined') return null;

  const byId = document.getElementById(id);
  if (byId instanceof HTMLLinkElement) return byId;

  const targetPath = normalizeStylesheetPath(href);
  const links = document.querySelectorAll('link[rel="stylesheet"]');
  for (let i = 0; i < links.length; i += 1) {
    const link = links[i];
    if (!(link instanceof HTMLLinkElement)) continue;
    if (normalizeStylesheetPath(link.href) === targetPath) return link;
  }

  return null;
}

export function injectWebStylesheet(href: string, id: string): void {
  if (typeof document === 'undefined') return;

  const existing = findExistingStylesheetLink(href, id);
  if (existing) {
    if (!existing.id) existing.id = id;
    // Refresh query string so layout CSS updates during HMR / version bumps
    if (existing.href !== new URL(href, window.location.origin).href) {
      existing.href = href;
    }
    return;
  }

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

function injectStylesheet(href: string, id: string): void {
  injectWebStylesheet(href, id);
}

import { scheduleIdleTask } from '@utils/scheduleIdleTask';

/** App-wide base styles — shell + theme blocking in index.html; full bundle deferred. */
export function loadGlobalWebStyles(): void {
  loadOnce('global-web', () => {
    injectStylesheet('/css/style-shell.css', 'smartsaas-app-shell-css');
    scheduleDeferredGlobalStyles();
  });
}

/** Login screen layout — inject on Login route only (not global index.html). */
export function loadLoginScreenStyles(): void {
  // Always ensure the current version is linked (loadOnce would skip version bumps)
  injectStylesheet('/css/login-screen.web.css?v=20261013a', 'smartsaas-login-screen-css');
  loaded.add('login-screen');
}

/** Remove login-only assets when leaving the login route (SPA navigation). */
export function unloadLoginScreenStyles(): void {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  loaded.delete('login-screen');
  document.getElementById('smartsaas-login-screen-css')?.remove();
}

export function loadHeaderBarStyles(): void {
  loadOnce('header-bar', () => {
    injectStylesheet('/css/header-bar.web.css', 'smartsaas-header-bar-css');
  });
}

export function loadReportsLibStyles(): void {
  loadOnce('reports-lib', () => {
    injectStylesheet('/css/lib/index.css', 'smartsaas-reports-lib-css');
  });
}

export function loadDataGridStyles(): void {
  loadOnce('reactdatagrid', () => {
    require('@inovua/reactdatagrid-community/index.css');
    require('@inovua/reactdatagrid-community/theme/default-light.css');
  });
}

export function loadDataScreenLayoutStyles(): void {
  loadOnce('data-screen-layout', () => {
    injectStylesheet('/css/data-screen-layout.css', 'smartsaas-data-screen-layout-css');
  });
}

export function loadInputToolbarDarkModeStyles(): void {
  loadOnce('input-toolbar-dark', () => {
    injectStylesheet('/css/input-toolbar-dark-mode.css', 'smartsaas-input-toolbar-dark-css');
  });
}

export function loadRowComponentStyles(): void {
  loadOnce('row-component', () => {
    injectStylesheet('/css/row-component.web.css', 'smartsaas-row-component-css');
  });
}

export function loadSchemaFormStyles(): void {
  loadOnce('schema-form-v2', () => {
    injectStylesheet('/css/schema-form.web.css?v=20260523d', 'smartsaas-schema-form-css');
  });
}
